import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  inject,
  LOCALE_ID,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { AuthModule as Auth0Module } from '@auth0/auth0-angular';
import { FullCalendarModule } from '@fullcalendar/angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AuthModule } from '@techspert-io/auth';
import { ClientsModule } from '@techspert-io/clients';
import { ConferencesModule } from '@techspert-io/conferences';
import { AppPaginatorIntl } from '@techspert-io/design-system';
import { ErrorHandlingModule } from '@techspert-io/errorHandling';
import { ExpertsModule } from '@techspert-io/experts';
import { OpportunitiesModule } from '@techspert-io/opportunities';
import { SolicitationModule } from '@techspert-io/solicitation';
import { NgxGoogleAnalyticsModule } from 'ngx-google-analytics';
import { environment } from '../environments/environment';
import { AdminPortalModule } from './admin-portal/admin-portal.module';
import { ClientOpportunityModule } from './admin-portal/clients/client-opportunity.module';
import { PaymentsModule } from './admin-portal/payments/payments.module';
import { AppComponent } from './app.component';
import { ROUTES } from './app.routes';
import { CoreModule } from './core/core.module';
import { EngagementsModule } from './features/engagements/engagements.module';
import { ExpertProfilesModule } from './features/expert-profiles/expert-profiles.module';
import { ExpertSourcesModule } from './features/expert-sources/expert-sources.module';
import { PORTAL_URL } from './features/opportunities/pipes/portal-links.pipe';
import { SEARCH_API_BASE_URL } from './features/search/services/search.service';
import { metaReducers, reducers } from './reducers';
import { BtxSharedModule } from './shared/btx-shared.module';
import { SHARED_MAILBOX } from './shared/components/schedule/email/email.component';
import { SALESFORCE_URL } from './shared/pipes/salesforce-links.pipe';
import { provideProfiling } from './shared/profiling/provide-profiling';
import { CurrencyService } from './shared/services/currency/currency.service';
import { FRESHDESK_URL } from './shared/tokens/freshdesk-url.token';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    FullCalendarModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    BtxSharedModule,
    CoreModule,
    AdminPortalModule,
    PaymentsModule,
    ClientOpportunityModule,
    AuthModule.forRoot(
      environment.auth0.domain,
      environment.connectApiBaseUrl,
      environment.base
    ),
    Auth0Module.forRoot({
      ...environment.auth0,
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
      authorizationParams: {
        ...environment.auth0.authorizationParams,
        redirect_uri: window.location.origin,
      },
    }),
    ClientsModule,
    ExpertsModule,
    ExpertProfilesModule,
    OpportunitiesModule,
    ExpertSourcesModule,
    ConferencesModule,
    EngagementsModule,
    SolicitationModule,
    RouterModule.forRoot(ROUTES, {
      useHash: true,
    }),
    NgxGoogleAnalyticsModule.forRoot(environment.gaMeasurementId),
    ErrorHandlingModule.forRoot(
      environment.raygunApiKey,
      environment.appVersion
    ),
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot(),
    !environment.production
      ? StoreDevtoolsModule.instrument({ maxAge: 25, connectInZone: true })
      : [],
  ],
  providers: [
    {
      provide: PORTAL_URL,
      useValue: environment.portalBase,
    },
    {
      provide: SALESFORCE_URL,
      useValue: environment.salesforceBase,
    },
    {
      provide: FRESHDESK_URL,
      useValue: environment.freshdeskBaseUrl,
    },
    {
      provide: SEARCH_API_BASE_URL,
      useValue: environment.searchApiBaseUrl,
    },
    {
      provide: SHARED_MAILBOX,
      useValue: environment.sharedMailbox,
    },
    {
      provide: LOCALE_ID,
      useValue:
        `${navigator.language}`.toLowerCase() === 'en-us'
          ? navigator.language
          : 'en-GB',
    },
    provideAppInitializer(() => inject(CurrencyService).init()),
    ...provideProfiling(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: MatPaginatorIntl, useClass: AppPaginatorIntl },
  ],
})
export class AppModule {}
